/**
 * The one line the user sees when the Find-person step cannot save (PREC-4017).
 *
 * The step's save used to await two RTK mutations with no catch, so a rejected request — on
 * Talent2Go's list 6746 a 404 "Search not found", because the duplicated list's apollo had no
 * company — became an unhandled rejection: no toast, no navigation, "Next does nothing". The
 * reason the API gave is the fastest way to a diagnosis, so it is appended to the translated
 * fallback rather than hidden behind it.
 */

interface ErrorLike {
	/** RTK `FetchBaseQueryError` payload — a Nest error body, or the raw text of a non-JSON reply. */
	data?: unknown;
	/** `FetchBaseQueryError` transport failures (`FETCH_ERROR`, `PARSING_ERROR`) carry the cause here. */
	error?: unknown;
	message?: unknown;
}

/** The reason the API or the transport gave for a refused request, verbatim, or undefined. */
export function requestFailureReason(error: unknown): string | undefined {
	return readReason(error);
}

function readReason(error: unknown): string | undefined {
	if (!error || typeof error !== "object") {
		return undefined;
	}

	const { data, error: transportError, message } = error as ErrorLike;
	const body = data && typeof data === "object" ? (data as { message?: unknown }).message : data;

	for (const candidate of [body, transportError, message]) {
		if (Array.isArray(candidate) && candidate.length > 0) {
			return candidate.map(String).join("; ");
		}

		if (typeof candidate === "string" && candidate.trim() !== "") {
			return candidate;
		}
	}

	return undefined;
}

/** `fallback`, followed by the reason the API or the transport gave when there is one. */
export function describeSaveFailure(error: unknown, fallback: string): string {
	const reason = readReason(error);

	return reason ? `${fallback} ${reason}` : fallback;
}
